import { Head } from "@inertiajs/react";

import { FinalCta } from "@/components/final-cta";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";

const EFFECTIVE_DATE = "March 7, 2026";

export default function Terms() {
  return (
    <>
      <Head title="Terms of Service" />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <PublicHeader />

        {/* Terms of Service Content */}
        <div className="container mx-auto max-w-4xl px-4 py-12">
          <div className="rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
            <h1 className="mb-8 text-3xl font-bold text-gray-900 dark:text-white">Terms of Service</h1>

            <div className="prose max-w-none space-y-6 text-gray-600 dark:text-gray-300">
              <p>
                These terms apply when you use OG Pilot to generate a one-off social preview, connect a domain for
                ongoing automation, install a supported integration such as the WordPress plugin, or call the API from
                your own application. They are written around how the product actually works in production: you send us
                page information and brand inputs, we generate or host a share image, and social platforms decide when
                to fetch or refresh that image.
              </p>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">1. Scope and Acceptance</h2>
                <p>
                  By using OG Pilot, you agree to these Terms of Service and our Privacy Policy. If you use OG Pilot on
                  behalf of a company, agency, or client, you represent that you have authority to bind that
                  organization to these terms. If you do not agree, do not use the service.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">2. What OG Pilot Does</h2>
                <p>
                  OG Pilot is software for creating and serving Open Graph and social preview images. Customers
                  typically use it in three ways: pasting a URL for a one-off image, verifying a domain for ongoing
                  generation, or sending signed API or SDK requests from their own stack. We may also offer supported
                  integrations, including an official WordPress plugin. The visual result can change when your source
                  page changes, you adjust settings, or upstream model providers change their behavior.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  3. Accounts, Teams, and Connected Domains
                </h2>
                <p>
                  You must be at least 18 years old (or the age of legal majority where you live) and capable of
                  entering a binding contract to use OG Pilot. You are responsible for safeguarding your credentials,
                  API keys, OAuth connections, and any activity performed by teammates or connected CMS installations
                  under your account.
                </p>
                <p>
                  If you connect a domain, you must complete the verification flow required for that connection, such as
                  DNS TXT verification or OAuth-based authorization. If verification stops working, ownership becomes
                  unclear, or we detect abuse, we may pause generation for that domain until the issue is resolved.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  4. Your Inputs and Publishing Responsibilities
                </h2>
                <p>
                  You may submit URLs, titles, descriptions, screenshots, logos, color values, prompts, and other brand
                  materials so OG Pilot can create a preview image. You represent that you have the right to use those
                  materials and that your use of the service complies with applicable law, platform rules, and
                  third-party rights.
                </p>
                <p>
                  Do not use OG Pilot to impersonate a brand, publish misleading previews, or generate assets for
                  domains you do not control or have permission to manage. You remain responsible for the final image
                  you publish, including claims about pricing, availability, dates, people, locations, regulated
                  products, or anything else shown in the preview.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">5. AI Output Realities</h2>
                <p>
                  OG Pilot can produce a strong first draft quickly, but AI image generation is not perfect. A render
                  can crop a logo badly, pull stale metadata from a page, hallucinate a background detail, or create a
                  layout that does not match your design system. We do not promise perfect brand fidelity, factual
                  accuracy, or performance outcomes from any generated asset.
                </p>
                <p>
                  Review every output before publishing it, especially if the image references product claims, event
                  details, prices, sponsor names, guest names, or legal disclosures. If a preview needs human editing
                  before it is safe or on-brand, that review is your responsibility.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  6. Hosting, Caching, and Delivery Behavior
                </h2>
                <p>
                  Social previews involve both OG Pilot and the platforms that fetch your <code>og:image</code>. We may
                  queue, retry, or re-render assets, and a hosted image URL may temporarily serve the last successful
                  render while a new one is processing. That behavior is part of running a production image service.
                </p>
                <p>
                  We do not control when X, LinkedIn, Slack, Facebook, or other crawlers refresh a cached image. A new
                  render on our side does not guarantee instant propagation everywhere.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  7. Billing, Subscriptions, and Refunds
                </h2>
                <p>
                  Pricing is shown in the product at the time of purchase. We may charge one-off fees, recurring
                  subscriptions, or credit top-ups, depending on the workflow you choose. Taxes may apply based on your
                  billing location.
                </p>
                <p>
                  If a paid generation succeeds and the asset is delivered or made available at its hosted URL, that
                  order is treated as fulfilled. Because generative work starts immediately, completed purchases are
                  generally non-refundable unless required by law. If a technical issue on our side prevents delivery,
                  contact support@ogpilot.com and we will investigate and, when appropriate, replace the generation or
                  issue a refund.
                </p>
                <p>
                  For subscription-based features and integrations such as the WordPress plugin, access checks are
                  enforced server-side. If your plan lapses, new generation requests may be paused while existing hosted
                  assets continue to resolve from cache or storage.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">8. Ownership and Licenses</h2>
                <p>
                  You keep ownership of the inputs you provide. Subject to full payment, OG Pilot assigns to you any
                  rights we may have in the final generated output, except for our software, templates, interface
                  design, trademarks, and other OG Pilot materials.
                </p>
                <p>
                  We may retain copies of inputs, logs, and outputs for backup, support, abuse prevention, service
                  improvement, analytics, and legal compliance. You are responsible for clearing any approvals,
                  releases, or licenses needed for the way you publish and distribute your finished assets.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  9. Third-Party Providers and Integrations
                </h2>
                <p>
                  OG Pilot depends on third-party infrastructure, including hosting, CDN delivery, payment processors,
                  analytics tools, authentication providers, and AI model vendors. Supported integrations and
                  marketplaces may also apply their own packaging, review, or policy requirements.
                </p>
                <p>
                  We are not responsible for outages, policy changes, or acts and omissions of those third parties, and
                  your use of their services remains subject to their own terms and privacy notices.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  10. Acceptable Use, Suspension, and Termination
                </h2>
                <p>
                  You may not use OG Pilot to break the law, evade plan limits, interfere with the service, reverse
                  engineer non-public parts of the platform, or generate content that infringes intellectual property,
                  privacy, publicity, or contractual rights.
                </p>
                <p>
                  We may suspend or terminate access if your use creates security risk, repeated abuse, unpaid balances,
                  or legal exposure for OG Pilot or other customers. We may also remove or block content when we are
                  required to do so by law or by a binding platform obligation.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  11. Changes to the Service and These Terms
                </h2>
                <p>
                  We improve OG Pilot continuously, which means features, supported integrations, AI providers, limits,
                  and pricing may change over time. We may update these terms as the product or legal requirements
                  evolve. When we make a material change, we will update the effective date below and provide notice
                  when required by law.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  12. Disclaimer, Liability Cap, and Indemnity
                </h2>
                <p>
                  OG Pilot is provided &quot;as is&quot; and &quot;as available.&quot; To the fullest extent permitted
                  by law, we disclaim warranties of merchantability, fitness for a particular purpose, non-infringement,
                  and uninterrupted availability. We do not guarantee that the service will be error-free or that any
                  image will achieve a particular branding, distribution, SEO, or click-through outcome.
                </p>
                <p>
                  To the fullest extent permitted by law, OG Pilot will not be liable for indirect, incidental, special,
                  consequential, exemplary, or punitive damages, or for lost profits, revenue, goodwill, or data. Our
                  total liability for any claim relating to these terms or the service will not exceed the amount you
                  paid to OG Pilot in the six months before the event giving rise to the claim.
                </p>
                <p>
                  You agree to defend, indemnify, and hold harmless OG Pilot from claims, damages, and expenses
                  resulting from your inputs, your published assets, your connected domains, or your violation of these
                  terms or applicable law.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">13. Governing Law and Contact</h2>
                <p>
                  These terms are governed by the laws of Romania, without regard to conflict of law principles. Any
                  disputes must be resolved in the courts located in Timisoara, Romania, and you consent to their
                  jurisdiction and venue.
                </p>
                <p>Questions about these terms can be sent to support@ogpilot.com.</p>
              </div>

              <p className="border-t border-gray-200 pt-8 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                Effective as of {EFFECTIVE_DATE}.
              </p>
            </div>
          </div>
        </div>

        <FinalCta
          title="Need share images that ship on time?"
          description="Use OG Pilot when a page needs a better preview fast: paste a URL for a one-off render, connect a verified domain for automation, or wire image generation into your publishing flow with the API."
          highlights={[
            { icon: "🌐", text: "Verified domain automation" },
            { icon: "⚙️", text: "One-off and API workflows" },
          ]}
          submitLabel="Try OG Pilot"
        />
      </div>
      <SiteFooter />
    </>
  );
}
